import { clamp } from '../core/math';
import { TICK_DT } from '../physics/constants';
import type { Controls } from '../physics/vehicle';

export const BIT_LEFT = 1;
export const BIT_RIGHT = 2;
export const BIT_BRAKE = 4;

const BRAKE_RISE = 0.14;
/** Release is the trail-off. Long enough to be a usable window, not a cliff. */
const BRAKE_FALL = 0.12;
const STEER_RISE = 0.16;
const STEER_FALL = 0.22;

/** Bytes of trace per physics tick: steer (int8) + brake (uint8). */
export const TRACE_STRIDE = 2;

/**
 * Quantise a control pair the way the recorder does.
 *
 * The *live* run is quantised too, not just the replay. Storing full floats and
 * replaying rounded ones would let a replay drift from the lap that produced it;
 * rounding both means the recorded bytes are exactly what the physics saw.
 *
 * int8 steer is ~0.008 resolution and uint8 brake ~0.004 — far finer than a
 * human or a 120 Hz tick can express.
 */
export function quantiseSteer(v: number): number {
  return Math.round(clamp(v, -1, 1) * 127);
}

export function quantiseBrake(v: number): number {
  return Math.round(clamp(v, 0, 1) * 255);
}

/** Drag multiplier quantised to a byte, so the live run and its replay see
 *  the identical value — the same reason the control pair is quantised. */
export function quantiseDrag(scale: number): number {
  return Math.max(0, Math.min(255, Math.round(scale * 255)));
}

export function dequantiseDrag(byte: number): number {
  return byte / 255;
}

export function dequantise(steerByte: number, brakeByte: number, out: Controls): Controls {
  out.steer = steerByte / 127;
  out.brake = brakeByte / 255;
  // Auto-throttle: the kart drives itself and braking is the whole decision,
  // which keeps the scheme to two inputs like the game this is modelled on.
  out.throttle = out.brake > 0.02 ? 0 : 1;
  // Reverse is re-derived every tick by the session from (brake, speed), so it
  // never belongs in the recorded trace — reset here so a following input can't
  // leave a stale reverse on.
  out.reverse = 0;
  return out;
}

/**
 * Ramps digital key presses into analogue controls.
 *
 * Lives on the input device rather than in the replay path: a gamepad already
 * supplies analogue values and must not be smoothed a second time, while a
 * keyboard brake is binary and needs the ramp to make trail braking expressible
 * with one key. Releasing gradually is how you trail off into an apex.
 */
export class KeyboardSmoother {
  private brakePressure = 0;
  private steerValue = 0;

  reset(): void {
    this.brakePressure = 0;
    this.steerValue = 0;
  }

  step(left: boolean, right: boolean, brake: boolean, dt: number = TICK_DT): {
    steer: number;
    brake: number;
  } {
    const target = (right ? 1 : 0) - (left ? 1 : 0);
    if (target === 0) {
      const decay = dt / STEER_FALL;
      this.steerValue -= clamp(this.steerValue, -decay, decay);
    } else {
      // Crossing the centre is faster than building lock, so a direction change
      // does not feel like it sticks.
      const rate = this.steerValue * target < 0 ? (dt / STEER_RISE) * 2.2 : dt / STEER_RISE;
      this.steerValue = clamp(this.steerValue + target * rate, -1, 1);
    }

    const rate = brake ? dt / BRAKE_RISE : -dt / BRAKE_FALL;
    this.brakePressure = clamp(this.brakePressure + rate, 0, 1);

    return { steer: this.steerValue, brake: this.brakePressure };
  }
}

/** Legacy helper kept for the v1 bitfield tests and the autopilot. */
export class ControlSmoother {
  private kb = new KeyboardSmoother();
  readonly controls: Controls = { steer: 0, throttle: 1, brake: 0, reverse: 0 };

  reset(): void {
    this.kb.reset();
    this.controls.steer = 0;
    this.controls.brake = 0;
    this.controls.throttle = 1;
  }

  apply(bits: number, dt: number = TICK_DT): Controls {
    const { steer, brake } = this.kb.step(
      (bits & BIT_LEFT) !== 0,
      (bits & BIT_RIGHT) !== 0,
      (bits & BIT_BRAKE) !== 0,
      dt,
    );
    return dequantise(quantiseSteer(steer), quantiseBrake(brake), this.controls);
  }
}
